import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import type { Store, UnknownAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

const SENTRY_CAPTURE_STATUSES = new Set([400, 403, 404, 422, 429, 500, 502, 503]);
const AUTH_ROUTE_PATTERNS = [
  /\/login[-_]user/i, /\/login[-_]vendor/i, /\/signup[-_]user/i,
  /\/signup[-_]vendor/i, /\/create[-_]user/i, /\/create[-_]vendor/i,
  /\/refresh[-_]token/i, /\/google[-_]verify/i, /\/google[-_]complete/i,
  /\/verify[-_]otp/i, /\/resend[-_]otp/i, /\/forgot[-_]password/i,
  /\/reset[-_]password/i,
];
const isAuthRoute = (url?: string) => Boolean(url && AUTH_ROUTE_PATTERNS.some(pattern => pattern.test(url)));
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

interface AuthSession { accessToken: string | null; refreshToken: string | null; identity: string | null }
interface SessionRequest extends InternalAxiosRequestConfig { _retry?: boolean; _authSession?: AuthSession }
interface RefreshCoordinator {
  pending: Map<string, Promise<AuthSession>>;
  completed: { source: AuthSession; target: AuthSession } | null;
}
// A role's existing API/product/auction instances must share the same refresh.
// A different store or role refresh URL has its own coordinator.
const coordinators = new WeakMap<object, Map<string, RefreshCoordinator>>();
const sameSession = (left: AuthSession, right: AuthSession) =>
  left.accessToken === right.accessToken && left.refreshToken === right.refreshToken && left.identity === right.identity;
const sessionKey = (session: AuthSession) => JSON.stringify([session.identity, session.accessToken, session.refreshToken]);
const changedSession = () => new Error("Your sign-in session changed. Please repeat the request.");

export const attachInterceptors = <State>(
  instance: AxiosInstance,
  store: Store<State>,
  getToken: (state: State) => string | null,
  getRefreshToken: (state: State) => string | null,
  setTokensAction: (payload: { accessToken: string; refreshToken: string }) => UnknownAction,
  logoutAction: () => UnknownAction,
  refreshUrl: string,
): void => {
  let byUrl = coordinators.get(store);
  if (!byUrl) { byUrl = new Map(); coordinators.set(store, byUrl); }
  let shared = byUrl.get(refreshUrl);
  if (!shared) { shared = { pending: new Map(), completed: null }; byUrl.set(refreshUrl, shared); }
  const coordinator = shared;
  const role = /\/vendor\//i.test(refreshUrl) ? "vendor" : /\/user\//i.test(refreshUrl) ? "user" : null;
  const readSession = (): AuthSession => {
    const state = store.getState();
    const slice = role ? record(record(state)[role]) : {};
    const id = role ? record(slice[role])._id : null;
    return { accessToken: getToken(state), refreshToken: getRefreshToken(state), identity: typeof id === "string" ? `${role}:${id}` : null };
  };

  instance.interceptors.request.use(config => {
    const request = config as SessionRequest;
    const current = readSession();
    // Retrying an old action with a newly logged-in account is never safe,
    // even when the new login belongs to the same database user.
    if (request._retry && request._authSession && !sameSession(current, request._authSession)) throw changedSession();
    request._authSession = current;
    if (current.accessToken) request.headers.Authorization = `Bearer ${current.accessToken}`;
    return request;
  }, error => Promise.reject(error));

  const refresh = (source: AuthSession): Promise<AuthSession> => {
    const key = sessionKey(source);
    const running = coordinator.pending.get(key);
    if (running) return running;
    const request = Promise.resolve().then(async () => {
      try {
        // Plain axios avoids recursively intercepting the refresh endpoint.
        const response = await axios.post(refreshUrl, { refreshToken: source.refreshToken });
        if (!sameSession(readSession(), source)) throw changedSession();
        const data = record(response.data);
        const token = data.accessToken || data.token;
        if (typeof token !== "string" || !token.trim()) throw new Error("Invalid refresh response");
        const nextRefresh = typeof data.refreshToken === "string" && data.refreshToken.trim() ? data.refreshToken : source.refreshToken;
        if (!nextRefresh) throw new Error("Invalid refresh response");
        store.dispatch(setTokensAction({ accessToken: token, refreshToken: nextRefresh }));
        const target = readSession();
        if (target.identity !== source.identity || target.accessToken !== token || target.refreshToken !== nextRefresh) throw changedSession();
        coordinator.completed = { source, target };
        return target;
      } catch (error) {
        // Only the session that requested this refresh can be cleared. A slow
        // response/failure cannot replace or sign out a newer login.
        if (sameSession(readSession(), source)) store.dispatch(logoutAction());
        throw error;
      } finally {
        coordinator.pending.delete(key);
      }
    });
    coordinator.pending.set(key, request);
    return request;
  };

  instance.interceptors.response.use(response => response, async (failure: unknown) => {
    const error = failure as AxiosError<{ message?: unknown; error?: unknown }>;
    const original = error.config as SessionRequest | undefined;
    const status = error.response?.status;
    if (isAuthRoute(original?.url)) return Promise.reject(failure);
    const rawMessage = error.response?.data?.message || error.response?.data?.error;
    const message = typeof rawMessage === "string" ? rawMessage : "";
    const authRelated400 = status === 400 && /token|expired|invalid token|unauthorized|jwt|not authorized/i.test(message);
    if (!original || original._retry || !(status === 401 || authRelated400)) {
      if (status !== undefined && SENTRY_CAPTURE_STATUSES.has(status)) {
        Sentry.captureException(failure, {
          tags: { api_status: String(status), api_method: (original?.method || "UNKNOWN").toUpperCase() },
          extra: { url: original?.url, status, responseMessage: message || error.message },
        });
      }
      return Promise.reject(failure);
    }

    const source = original._authSession;
    if (!source || !source.refreshToken) return Promise.reject(failure);
    const current = readSession();
    let target: AuthSession;
    if (!sameSession(source, current)) {
      const completed = coordinator.completed;
      // A delayed 401 from the token we just refreshed can reuse that result.
      // An unrelated token/login change cannot revive the old request.
      if (!completed || !sameSession(completed.source, source) || !sameSession(completed.target, current)) return Promise.reject(failure);
      target = current;
    } else {
      original._retry = true;
      target = await refresh(source);
    }
    if (!sameSession(readSession(), target)) return Promise.reject(failure);
    original._retry = true;
    original._authSession = target;
    original.headers.Authorization = `Bearer ${target.accessToken}`;
    return instance(original);
  });
};
